/**
 * QA Perspective Mutation Guards
 *
 * SECURITY CRITICAL: These guards prevent mutations when QA perspective is active.
 *
 * WHY THIS EXISTS:
 * - Admins can view UI as other user types for testing
 * - But they must NOT accidentally perform actions under that perspective
 * - All mutations must be blocked unless perspective matches actual role
 *
 * SAFETY GUARANTEES:
 * - Mutations fail-closed (throw error if guard fails)
 * - Guards check both perspective state AND actual role
 * - Backend still enforces all permissions (this is defense-in-depth)
 */

import type { ActualRole, QAPerspective } from '@/types/qa-perspective'

export class QAPerspectiveMutationError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'QAPerspectiveMutationError'
  }
}

/**
 * Guard a mutation action to prevent execution under wrong perspective
 *
 * @param action - The mutation function to guard
 * @param requiredRole - The role required to perform this mutation
 * @param currentPerspective - Current QA perspective (null = using actual role)
 * @param actualRole - User's actual authenticated role
 * @returns Result of action if guard passes
 * @throws QAPerspectiveMutationError if perspective doesn't match required role
 *
 * @example
 * ```ts
 * const result = await guardMutation(
 *   () => api.deleteHouseholdMember(memberId),
 *   'household_owner',
 *   qaPerspective,
 *   actualRole
 * );
 * ```
 */
export async function guardMutation<T>(
  action: () => Promise<T>,
  requiredRole: ActualRole,
  currentPerspective: QAPerspective,
  actualRole: ActualRole,
): Promise<T> {
  // If no perspective is active (null), use actual role - this is safe
  if (currentPerspective === null) {
    return action()
  }

  // If perspective is active but doesn't match actual role, block mutation
  if (currentPerspective !== actualRole) {
    throw new QAPerspectiveMutationError(
      `Mutation blocked: QA perspective active (viewing as "${currentPerspective}" but authenticated as "${actualRole}"). ` +
        `Switch to your actual role or end QA session to perform this action.`,
    )
  }

  // Perspective matches actual role - safe to proceed
  return action()
}

/**
 * Synchronous version of guardMutation for non-async actions
 *
 * @example
 * ```ts
 * const result = guardMutationSync(
 *   () => navigate('/admin/delete-user'),
 *   'admin',
 *   qaPerspective,
 *   actualRole
 * );
 * ```
 */
export function guardMutationSync<T>(
  action: () => T,
  requiredRole: ActualRole,
  currentPerspective: QAPerspective,
  actualRole: ActualRole,
): T {
  if (currentPerspective === null) {
    return action()
  }

  if (currentPerspective !== actualRole) {
    throw new QAPerspectiveMutationError(
      `Mutation blocked: QA perspective active (viewing as "${currentPerspective}" but authenticated as "${actualRole}"). ` +
        `Switch to your actual role or end QA session to perform this action.`,
    )
  }

  return action()
}

/**
 * Check if a mutation is allowed without throwing
 * Useful for UI disable/enable logic
 *
 * @returns true if mutation is allowed, false if blocked
 *
 * @example
 * ```ts
 * <Button
 *   disabled={!canMutate('household_owner', qaPerspective, actualRole)}
 *   onClick={handleDelete}
 * >
 *   Delete Member
 * </Button>
 * ```
 */
export function canMutate(
  requiredRole: ActualRole,
  currentPerspective: QAPerspective,
  actualRole: ActualRole,
): boolean {
  // No perspective active - mutations allowed
  if (currentPerspective === null) {
    return true
  }

  // Perspective active - only allow if it matches actual role
  return currentPerspective === actualRole
}

/**
 * Get a human-readable reason why mutation is blocked
 * Returns null if mutation is allowed
 */
export function getMutationBlockedReason(
  requiredRole: ActualRole,
  currentPerspective: QAPerspective,
  actualRole: ActualRole,
): string | null {
  if (currentPerspective === null) {
    return null
  }

  if (currentPerspective !== actualRole) {
    return `Cannot perform this action while viewing as "${currentPerspective}". Switch to your actual role (${actualRole}) first.`
  }

  return null
}
